import React from 'react';
import { Square } from '@grubhubprod/gh-cookbook-react';
import { useHistory } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { IErrorResponse } from 'src/utilities/shared-types';
import {
  SdkErrorResponse,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { employeeFormDefaultValues } from 'src/components/enterprise/features/Employee/components/Form/constants/EmployeeDefaultValues';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { PageTitle } from 'src/components/enterprise/layout/PageTitle/PageTitle';
import { useCreateEmployee } from 'src/components/enterprise/features/Employee/CreatePage/hooks/useCreateEmployee';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import {
  AnalyticsEventCategory,
  AnalyticsEventAction,
} from 'src/components/AnalyticsProvider/analyticsConstants';
import { EmployeeFormWrapper } from 'src/components/enterprise/features/Employee/components/EmployeeFormWrapper/EmployeeFormWrapper';
import { EmployeeFormProvider } from 'src/components/enterprise/features/Employee/components/Form/EmployeeFormProvider/EmployeeFormProvider';
import { Employee } from 'src/types/Employee';
import {
  buildCAUIPath,
  buildCMUIPath,
  CAUIPage,
  CMUIPage,
  getHomeBreadcrumb,
} from 'src/constants/urls';

export const EmployeeCreatePage: React.FC = () => {
  const { corpId } = useAuth();
  const { push } = useHistory();
  const { sendAnalytics } = useSendAnalytics({
    eventcategory: AnalyticsEventCategory.CORP_ADMIN_EMPLOYEE,
    eventaction: AnalyticsEventAction.ADD_NEW_EMPLOYEE,
  });

  const onSuccess = async (employee: Employee): Promise<void> => {
    sendAnalytics();
    pushSuccess(`${employee.firstName} has been added to your employees`);
    push(
      buildCMUIPath(CMUIPage.EMPLOYEE_DETAILS, {
        corpId,
        employeeId: employee.id,
      })
    );
  };

  const onError = async (
    error: IErrorResponse | SdkErrorResponse
  ): Promise<void> => {
    pushErrorFromAPI(error, {
      fallbackMessage: 'Employee was not added. Try again.',
    });
  };

  const { createEmployee } = useCreateEmployee({
    corpId,
    onSuccess,
    onError,
  });

  return (
    <Square inset="lg">
      <PageHeader>
        <PageTitle title="Add New Employee" />
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpId),
            },
            {
              label: 'Employees',
              href: buildCAUIPath(CAUIPage.EMPLOYEES, { corpId }),
            },
          ]}
        />
      </PageHeader>
      <EmployeeFormProvider>
        <EmployeeFormWrapper
          onSubmit={createEmployee}
          defaultValues={employeeFormDefaultValues}
        />
      </EmployeeFormProvider>
    </Square>
  );
};
